import { Check, CalendarClock, CalendarCheck, Ban, StickyNote } from 'lucide-react'
import { Chip } from './Chip'
import { DecisionActions, type OutcomeAction } from './DecisionActions'
import {
  type InterviewRow,
  interviewOutcome,
  roundLabel,
  isActionable,
  formatDateTime,
} from '../../utils/interviews'

export type { OutcomeAction }

export function InterviewCard({
  interview,
  applicantName,
  canAct,
  busy,
  onSubmit,
}: {
  interview: InterviewRow
  applicantName: string
  canAct: boolean
  busy: boolean
  onSubmit: (interviewId: string, action: OutcomeAction, notes: string | null) => Promise<void>
}) {
  const outcome = interviewOutcome(interview)
  const actionable = isActionable(interview)
  const showActions = canAct && actionable

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold text-foreground">{roundLabel(interview.round)}</h4>
        <Chip tone={outcome.tone}>{outcome.label}</Chip>
      </div>

      <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
        <div className="flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" />
          <span className="text-foreground/70">Scheduled:</span> {formatDateTime(interview.scheduled_at)}
        </div>
        <div className="flex items-center gap-1.5">
          <CalendarCheck className="h-3.5 w-3.5" />
          <span className="text-foreground/70">Booked:</span> {formatDateTime(interview.booked_at)}
        </div>
        {interview.completed_at && (
          <div className="flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" />
            <span className="text-foreground/70">Completed:</span> {formatDateTime(interview.completed_at)}
          </div>
        )}
        {interview.canceled_at && (
          <div className="flex items-center gap-1.5">
            <Ban className="h-3.5 w-3.5" />
            <span className="text-foreground/70">Canceled:</span> {formatDateTime(interview.canceled_at)}
          </div>
        )}
      </div>

      {interview.canceled_at && interview.cancellation_reason && (
        <p className="mt-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Cancellation reason: </span>
          {interview.cancellation_reason}
        </p>
      )}

      {interview.notes && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{interview.notes}</span>
        </p>
      )}

      {showActions && (
        <div className="mt-4 border-t border-border pt-3">
          <DecisionActions
            interviewId={interview.id}
            applicantName={applicantName}
            round={interview.round}
            canAct={canAct}
            busy={busy}
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  )
}
